/**
 * Request Status Notification Component
 * Phase P2.2-6: Guest Feedback on Request Progress
 *
 * Lightweight overlay notification for guest request status updates.
 * Appears bottom-right, auto-dismisses after 6 seconds.
 * Non-blocking, no interaction required.
 */

import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Clock } from 'lucide-react';

interface RequestStatusNotificationProps {
  message: string | null;
  type: 'in_progress' | 'completed' | null;
  onDismiss: () => void;
}

export function RequestStatusNotification({ message, type, onDismiss }: RequestStatusNotificationProps) {
  // Auto-dismiss after 6 seconds
  useEffect(() => {
    if (!message) return;

    const timer = setTimeout(() => {
      onDismiss();
    }, 6000);

    return () => clearTimeout(timer);
  }, [message, onDismiss]);

  return (
    <AnimatePresence>
      {message && type && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed bottom-6 right-6 z-50 pointer-events-auto"
        >
          <div className="bg-slate-800/95 backdrop-blur-xl border border-slate-700 rounded-xl shadow-2xl p-4 pr-5 flex items-start gap-3 min-w-[280px] max-w-[400px]">
            {/* Icon */}
            <div className={`flex-shrink-0 ${type === 'in_progress' ? 'text-blue-400' : 'text-emerald-400'}`}>
              {type === 'in_progress' ? (
                <Clock className="w-5 h-5" />
              ) : (
                <CheckCircle className="w-5 h-5" />
              )}
            </div>

            {/* Message */}
            <div className="flex-1">
              <p className="text-white text-sm leading-relaxed">
                {message}
              </p>
            </div>
          </div>

          {/* Progress bar */}
          <motion.div
            initial={{ scaleX: 1 }}
            animate={{ scaleX: 0 }}
            transition={{ duration: 6, ease: 'linear' }}
            className="h-0.5 bg-gradient-to-r from-cyan-400 to-transparent origin-left mt-0.5 rounded-full"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
